/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Synchronisiert auf Produktseiten ein manuell ausgewähltes Galeriebild
 * mit der dazugehörigen Shopify-Variante.
 *
 * Voraussetzungen:
 * - Das <product-media>-Element besitzt sync-variant-on-media.
 * - Das Bild ist einer Variante zugeordnet oder verwendet eine
 *   Focal-Bildgruppe im Alt-Text nach dem Muster #Option_Wert.
 * - Alle anderen Optionen wie die Größe bleiben unverändert.
 */
(() => {
  const normalize = (value) =>
    String(value == null ? "" : value).trim().toLocaleLowerCase();

  const findVariantImageOptionIndex = (variantPicker) => {
    const selector = variantPicker.querySelector(
      '[data-selector-type="variant-image"]'
    );

    const input = selector
      ? selector.querySelector(
          'input[name^="option"], select[name^="option"]'
        )
      : null;

    const match = input ? input.name.match(/^option(\d+)$/) : null;

    return match ? Number(match[1]) - 1 : -1;
  };

  const findAltGroup = (product, media) => {
    const alt = media ? media.alt : null;

    if (!alt || !alt.includes("#")) {
      return null;
    }

    const groupDefinition = alt.split("#").pop();
    const separatorPosition = groupDefinition.indexOf("_");

    if (separatorPosition === -1) {
      return null;
    }

    const optionName = normalize(
      groupDefinition.slice(0, separatorPosition)
    );
    const optionValue = normalize(
      groupDefinition.slice(separatorPosition + 1)
    );

    const option = product.options.find(
      (item) => normalize(item.name) === optionName
    );

    if (!option) {
      return null;
    }

    return {
      optionIndex: option.position - 1,
      optionValue
    };
  };

  document.addEventListener(
    "flickity:slide-settled",
    async (event) => {
      const carousel = event.target.closest("flickity-carousel");
      const productMedia = carousel
        ? carousel.closest("product-media[sync-variant-on-media]")
        : null;

      if (!carousel || !productMedia || !productMedia.product) {
        return;
      }

      const flickity = await carousel.flickityInstance;
      const selectedSlide = flickity ? flickity.selectedElement : null;

      if (
        !selectedSlide ||
        selectedSlide.dataset.mediaType !== "image"
      ) {
        return;
      }

      const mediaId = Number(selectedSlide.dataset.mediaId);
      const formId = productMedia.getAttribute("form-id");

      if (!mediaId || !formId) {
        return;
      }

      const variantPickers = Array.from(
        document.querySelectorAll(
          `product-variants[form-id="${CSS.escape(formId)}"]`
        )
      );

      // Auf Produktseiten kann Focal neben dem sichtbaren Variantenwähler
      // einen Sticky-Wähler mit reinen Dropdowns ausgeben. Für die
      // Bildsynchronisierung muss gezielt der Wähler mit Variantenbildern
      // verwendet werden, sonst lässt sich die farbsteuernde Option nicht
      // eindeutig bestimmen.
      const variantPicker =
        variantPickers.find(
          (element) =>
            element.product &&
            element.selectedVariant &&
            findVariantImageOptionIndex(element) >= 0
        ) ||
        variantPickers.find(
          (element) => element.product && element.selectedVariant
        ) ||
        variantPickers[0];

      if (
        !variantPicker ||
        !variantPicker.product ||
        !variantPicker.selectedVariant
      ) {
        return;
      }

      const product = variantPicker.product;
      const currentVariant = variantPicker.selectedVariant;

      // Verhindert eine Ereignisschleife nach der Variantenauswahl.
      if (
        currentVariant.featured_media &&
        currentVariant.featured_media.id === mediaId
      ) {
        return;
      }

      let optionIndex = findVariantImageOptionIndex(variantPicker);
      let candidates = product.variants.filter(
        (variant) =>
          variant.featured_media &&
          variant.featured_media.id === mediaId
      );

      // Unterstützt zusätzliche Focal-Bildergruppen per Alt-Text.
      if (candidates.length === 0) {
        const media = product.media.find(
          (item) => item.id === mediaId
        );
        const altGroup = findAltGroup(product, media);

        if (!altGroup) {
          return;
        }

        optionIndex = altGroup.optionIndex;
        candidates = product.variants.filter(
          (variant) =>
            normalize(variant.options[optionIndex]) ===
            altGroup.optionValue
        );
      }

      if (optionIndex < 0 || candidates.length === 0) {
        return;
      }

      // Nur die bildgesteuerte Option darf sich verändern.
      const matchingVariants = candidates.filter((variant) =>
        variant.options.every((value, index) => {
          return (
            index === optionIndex ||
            value === currentVariant.options[index]
          );
        })
      );

      // Bei keiner oder mehreren passenden Varianten wird nicht geraten.
      if (matchingVariants.length !== 1) {
        return;
      }

      const targetVariant = matchingVariants[0];

      if (targetVariant.id === currentVariant.id) {
        return;
      }

      // Kein stiller Wechsel zu einer anderen ausverkauften Kombination.
      if (
        variantPicker.hideSoldOutVariants &&
        !targetVariant.available
      ) {
        return;
      }

      variantPicker.selectVariant(targetVariant.id);
    }
  );
})();
